/**
 * Footage Library — Learn-Mode inline tips.
 *
 * Covers `routes/social/FootageLibrary.tsx`, `_sections/footage/*`, and the
 * public guest-upload page `routes/public/FootageUpload.tsx`.
 */

import type { LearnTip } from "./types";

export const footageLibraryTips: LearnTip[] = [
  {
    key: "social-footage-library",
    title: "Footage Library",
    body: "Every raw clip available for this client — uploaded, pulled from their asset library, or archived from past Instagram posts — in one searchable place, shared by the video editor and Autopilot.",
  },
  {
    key: "social-footage-stats",
    title: "Library stats",
    body: "A quick read on how much footage exists, how much of it is indexed and searchable, and how much still needs a consent decision before it can be used.",
  },
  {
    key: "social-footage-filters",
    title: "Search & filters",
    body: "Search matches transcripts, captions, and tags. Kind, source, orientation and usable-for let you narrow down to exactly the clip you need.",
    bullets: [
      "Usable — organic: cleared for regular posts",
      "Usable — ads: also cleared for paid campaigns (a stricter bar)",
    ],
  },
  {
    key: "social-footage-grid",
    title: "Footage grid",
    body: "Click any tile to open its full detail — preview, transcript, segments, and consent controls.",
  },
  {
    key: "social-footage-detail",
    title: "Footage detail",
    body: "Preview the clip, read its transcript, jump to a segment by clicking it, edit tags, and manage consent — all in one panel.",
  },
  {
    key: "social-footage-find-clips",
    title: "Find clips",
    body: "Opens Quick Reel with this clip pre-selected and scans its transcript for moments worth turning into a reel — a triage pass, never a prediction of performance.",
  },
  {
    key: "social-footage-consent",
    title: "Consent",
    body: "Granting consent needs a grantor name plus either a signed date or a reference number — this is what proves the footage is cleared to use. Restricting or denying consent never needs paperwork.",
  },
  {
    key: "social-footage-upload-links",
    title: "Upload links",
    body: "Send a client or filmer a no-login link to upload raw footage straight into this library. The link shows once at creation — copy it before leaving this page.",
  },
  {
    key: "social-creator-delivery-links",
    title: "Creator delivery links",
    body: "A creator delivery link lets a photographer or videographer send a whole shoot — edited JPEGs, camera raw (CR2/CR3/DNG) and MP4/MOV clips — straight into this client's media library, in Raw footage & photos.",
    bullets: [
      "Standing monthly link: 30 days, up to 500 files, label like “Creator - Brand - Nov 2026”",
      "Big clips upload in resumable parts, three files at a time; macOS ._ files are skipped",
      "Files appear in the media library a few minutes after upload (tagged raw + creator-upload + the label)",
    ],
  },
  {
    key: "social-creator-delivery-files",
    title: "Send your photos & videos",
    body: "Choose files or a whole folder. Large videos upload in pieces, so a dropped connection only retries the piece that failed — press Retry and it picks up where it stopped.",
  },
  {
    key: "social-footage-upload-public",
    title: "Upload your footage",
    body: "Pick the video files you want to share — they upload directly and securely, and the team will review them before they're used anywhere.",
  },
];
